import { useEffect, useRef, useState } from 'react';
import { Button, FormField, Modal, Tabs, Textarea } from '@/components';

const MAX_JSON_BYTES = 5 * 1024 * 1024;

const IMPORT_TABS = [
  { id: 'upload', label: 'Upload File' },
  { id: 'paste', label: 'Paste JSON' },
];

const PASTE_PLACEHOLDER = `Paste your flow JSON here...

{
  "name": "WPAS Qualification Flow",
  "steps": [ ... ]
}`;

function isJsonFile(file) {
  if (!file) return false;
  return (
    file.type === 'application/json' ||
    file.name.toLowerCase().endsWith('.json')
  );
}

function validateFlowObject(parsed) {
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return 'Invalid JSON format: expected a flow object.';
  }
  const hasNodes = Array.isArray(parsed.nodes) && parsed.nodes.length > 0;
  const hasSteps = Array.isArray(parsed.steps) && parsed.steps.length > 0;
  if (!hasNodes && !hasSteps) {
    return "The flow JSON must contain at least one step or node in 'steps' or 'nodes'.";
  }
  return '';
}

function parseFlowFile(file) {
  return new Promise((resolve, reject) => {
    if (!isJsonFile(file)) {
      reject(new Error('Please upload a .json file.'));
      return;
    }
    if (file.size > MAX_JSON_BYTES) {
      reject(new Error('File is too large. Maximum size is 5 MB.'));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result);
        const invalid = validateFlowObject(parsed);
        if (invalid) {
          reject(new Error(invalid));
          return;
        }
        resolve(parsed);
      } catch (err) {
        const detail =
          err instanceof SyntaxError && err.message
            ? `Invalid JSON format: ${err.message}`
            : 'Invalid JSON format.';
        reject(new Error(detail));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file.'));
    reader.readAsText(file);
  });
}

function parsePastedJson(text) {
  const raw = String(text || '').trim();
  if (!raw) {
    throw new Error('Please paste flow JSON.');
  }
  if (new Blob([raw]).size > MAX_JSON_BYTES) {
    throw new Error('JSON is too large. Maximum size is 5 MB.');
  }
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    const detail =
      err instanceof SyntaxError && err.message
        ? `Invalid JSON format: ${err.message}`
        : 'Invalid JSON format.';
    throw new Error(detail);
  }
  const invalid = validateFlowObject(parsed);
  if (invalid) throw new Error(invalid);
  return parsed;
}

export function ImportFlowModal({
  isOpen,
  onClose,
  onSubmit,
  isPending = false,
  serverError = '',
  onClearError,
}) {
  const fileInputRef = useRef(null);
  const [tab, setTab] = useState('upload');
  const [file, setFile] = useState(null);
  const [jsonText, setJsonText] = useState('');
  const [localError, setLocalError] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (isOpen) return;
    setTab('upload');
    setFile(null);
    setJsonText('');
    setLocalError('');
    setIsDragging(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [isOpen]);

  const clearErrors = () => {
    onClearError?.();
    setLocalError('');
  };

  const applyFile = async (nextFile) => {
    if (!nextFile) return;
    clearErrors();
    try {
      await parseFlowFile(nextFile);
      setFile(nextFile);
    } catch (err) {
      setFile(null);
      setLocalError(err.message || 'Invalid JSON format.');
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (isPending) return;
    applyFile(e.dataTransfer.files?.[0]);
  };

  const handleTabChange = (nextTab) => {
    if (isPending) return;
    setTab(nextTab);
    clearErrors();
  };

  const handleSubmit = () => {
    try {
      if (tab === 'upload') {
        if (!file) {
          setLocalError('Please select a .json file.');
          return;
        }
        setLocalError('');
        onSubmit({ file });
        return;
      }
      const data = parsePastedJson(jsonText);
      setLocalError('');
      onSubmit({ data });
    } catch (err) {
      setLocalError(err.message || 'Invalid JSON format.');
    }
  };

  const canSubmit = tab === 'upload' ? Boolean(file) : Boolean(jsonText.trim());
  const error = localError || serverError;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Import Flow to Canvas"
      size="lg"
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isPending}
            className="h-10 px-5"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSubmit}
            disabled={isPending || !canSubmit}
            className="h-10 px-5"
          >
            {isPending ? 'Loading...' : 'Load on Canvas'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Tabs items={IMPORT_TABS} value={tab} onChange={handleTabChange} />

        {tab === 'upload' ? (
          <FormField label="JSON file" required error={error || undefined}>
            <div
              onDragOver={(e) => {
                e.preventDefault();
                if (!isPending) setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              className={`relative border rounded-[8px] px-4 py-6 flex flex-col items-center justify-center bg-white transition-colors ${
                isDragging
                  ? 'border-primary bg-surface-mint'
                  : 'border-[#D0D5D1] hover:bg-[#FAFAFA]'
              }`}
            >
              <input
                ref={fileInputRef}
                id="import-flow-file"
                type="file"
                accept=".json,application/json"
                disabled={isPending}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                onChange={(e) => applyFile(e.target.files?.[0])}
              />
              <div className="flex flex-col items-center gap-1.5 text-center pointer-events-none">
                <div className="w-8 h-8 rounded-[6px] bg-[#EBF4EA] flex items-center justify-center text-primary">
                  <svg
                    className="w-4 h-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                </div>
                {file ? (
                  <span className="text-[14px] font-medium text-text-primary">
                    {file.name}
                  </span>
                ) : (
                  <>
                    <p className="text-[14px] leading-[18px]">
                      <span className="font-semibold text-primary">
                        Click to upload
                      </span>{' '}
                      <span className="text-text-muted">or drag and drop</span>
                    </p>
                    <p className="text-[12px] text-text-muted">
                      JSON, max 5 MB
                    </p>
                  </>
                )}
              </div>
            </div>
          </FormField>
        ) : (
          <FormField htmlFor="import-flow-json" error={error || undefined}>
            <Textarea
              id="import-flow-json"
              rows={12}
              value={jsonText}
              onChange={(e) => {
                setJsonText(e.target.value);
                if (localError || serverError) clearErrors();
              }}
              placeholder={PASTE_PLACEHOLDER}
              disabled={isPending}
              error={Boolean(error)}
              className="font-mono text-[13px] min-h-[220px]"
            />
          </FormField>
        )}
      </div>
    </Modal>
  );
}
